import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Folder, Folders, GraduationCap, X } from "lucide-react";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import InitiativeRowCst from "@components/InitiativeRowCst";
import {
  InitiativeListItem,
  useInitiativesPaginatedList,
} from "@hooks/useInitiativesPaginatedList";
import { openToast } from "@utils/toastStore";
import { useAssociateInitiatives } from "./hooks/useAssociateInitiatives";
import { AssociateInitiativesTriggerPayload } from "./types";

const PAGE_SIZE = 10;

interface AssociateInitiativesModalProps {
  trigger: AssociateInitiativesTriggerPayload;
  onClose: () => void;
}

const AssociateInitiativesModal: React.FC<AssociateInitiativesModalProps> = ({
  trigger,
  onClose,
}) => {
  const { t } = useTranslation("associate-initiatives");
  const theme = useTheme();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    () => new Set(trigger.initialAssociatedIds),
  );

  const { items, totalItems, totalPages, isLoading } =
    useInitiativesPaginatedList({
      corporateId: trigger.corporateId,
      page,
      pageSize,
      search: search || undefined,
    });

  const { submit, isSubmitting } = useAssociateInitiatives();

  const handleToggle = (id: string | number, next: boolean) => {
    const numericId = typeof id === "string" ? Number(id) : id;
    setSelectedIds((prev) => {
      const copy = new Set(prev);
      if (next) copy.add(numericId);
      else copy.delete(numericId);
      return copy;
    });
  };

  const handleConfirm = async () => {
    try {
      await submit({
        trigger,
        currentIds: Array.from(selectedIds),
        originalIds: trigger.initialAssociatedIds,
      });
      trigger.onSuccess?.();
      onClose();
      openToast({
        severity: "success",
        message: t("feedback.success") || "Iniziative associate con successo",
      });
    } catch (e: any) {
      onClose();
      openToast({
        severity: "error",
        message:
          e?.message ||
          t("feedback.error") ||
          "Errore durante l'associazione delle iniziative",
      });
    }
  };

  const selectedIdsForRender = useMemo<Set<string | number>>(
    () => new Set(Array.from(selectedIds)),
    [selectedIds],
  );

  const ObjectIcon =
    trigger.objectType === "course"
      ? Folder
      : trigger.objectType === "path"
        ? Folders
        : GraduationCap;

  return (
    <Dialog
      open
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle
        sx={{ padding: "20px 24px 0", color: theme.palette.text.primary }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {trigger.objectTitle && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: theme.palette.text.primary,
                }}
              >
                <ObjectIcon size={16} />
                <Typography sx={{ fontSize: "13px" }}>
                  {trigger.objectTitle}
                </Typography>
              </Box>
            )}

            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                color: theme.palette.text.primary,
                mt: 0.5,
              }}
            >
              {t("title") || "Associa Iniziative"}
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            aria-label="close"
            disabled={isSubmitting}
            sx={{ color: theme.palette.text.primary, flexShrink: 0 }}
          >
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "8px 24px 24px",
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
            color: theme.palette.text.primary,
          }}
        >
          <Typography sx={{ fontSize: "13px" }}>
            {t("selected-count", { count: selectedIds.size }) ||
              `${selectedIds.size} elementi selezionati`}
          </Typography>
        </Box>

        <SearchableMultiSelectListCst<InitiativeListItem>
          items={items}
          getId={(i) => i.id}
          renderRow={({ item }) => <InitiativeRowCst initiative={item} />}
          selectedIds={selectedIdsForRender}
          onToggle={handleToggle}
          initialSearchValue={search}
          onSearchSubmit={(v) => {
            setSearch(v);
            setPage(1);
          }}
          searchPlaceholder={t("search-placeholder") || "Nome"}
          searchButtonLabel={t("search-button") || "Cerca"}
          liveSearch
          page={page}
          pageSize={pageSize}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          isLoading={isLoading}
          showSelectedCount={false}
          selectedCountLabel=""
          emptyLabel={t("empty") || "Nessuna iniziativa trovata"}
        />
      </DialogContent>

      <DialogActions sx={{ padding: "12px 24px 20px" }}>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isSubmitting}
          sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 700 }}
        >
          {isSubmitting
            ? t("submitting") || "Salvataggio…"
            : t("confirm") || "Conferma"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssociateInitiativesModal;
